import type { InSnipCard } from "../types";
import { deepDiveFor } from "../deepDive";
import { isPlaceholderLabel } from "../plateFacts";

/**
 * "Ask the story" follow-up chips: grounded question and answer pairs derived entirely from
 * content the card already carries (deep dive beats and questions, catch-up primer, key facts,
 * jargon notes). Zero runtime AI cost, instant, available to anonymous readers. The final chip
 * in the UI hands off to the real Ask AI for a live question.
 *
 * Everything here is fail-closed: a card without enough grounded material simply gets no chips.
 * House style: no em dashes or en dashes anywhere in copy.
 */

export type FollowUpKind = "before" | "next" | "facts" | "term" | "authored";

export interface FollowUp {
  q: string;
  a: string;
  kind: FollowUpKind;
}

const MIN_ANSWER_CHARS = 30;

function clean(text: string | undefined | null): string {
  return (text || "").replace(/\s+/g, " ").trim();
}

function usable(text: string): boolean {
  return text.length >= MIN_ANSWER_CHARS;
}

/** Derive up to `max` grounded follow-up chips for a card. */
export function deriveFollowUps(
  card: Pick<
    InSnipCard,
    "id" | "deepDive" | "catchUp" | "keyPoints" | "notes" | "whyItMatters"
  > | null | undefined,
  max = 3,
): FollowUp[] {
  if (!card) return [];
  const out: FollowUp[] = [];
  const seen = new Set<string>();
  const push = (q: string, a: string, kind: FollowUpKind) => {
    const question = clean(q);
    const answer = clean(a);
    const key = question.toLowerCase();
    if (!question || !usable(answer) || seen.has(key)) return;
    seen.add(key);
    out.push({ q: question, a: answer, kind });
  };

  const dive = deepDiveFor(card);

  // 1. What led to this? The catch-up primer, else the background beat.
  const before =
    (card.catchUp || []).map(clean).filter(Boolean).join(" ") ||
    clean(dive?.beats.find((b) => b.phase === "background")?.line);
  push("What led to this?", before, "before");

  // 2. What happens next? The forward-looking beat.
  const next = clean(dive?.beats.find((b) => b.phase === "next")?.line);
  push("What happens next?", next, "next");

  // 3. Authored deep-dive questions that already carry a grounded answer.
  for (const qa of dive?.questions || []) {
    if (qa.a) push(qa.q, qa.a, "authored");
  }

  // 4. The key numbers, composed from the fact strip. ONLY when the strip really is numbers.
  //    The dive generator's source-only fallback labels whole sentences "Reported fact 1", and
  //    composing those as "label: value" produced an answer that literally read "Reported fact
  //    1: <the headline>. Reported fact 2: ...". A chip that promises numbers must have numbers;
  //    otherwise the story's own beats below are the honest answer.
  const realFacts = (dive?.keyFacts || []).filter((f) => clean(f.label) && !isPlaceholderLabel(f.label));
  const facts = realFacts.map((f) => `${clean(f.label)}: ${clean(f.value)}`).filter((s) => s.length > 3);
  if (facts.length >= 2) push("What are the key numbers?", facts.join(". ") + ".", "facts");

  // 4b. What the reader loses when 4 cannot fire: a grounded chip from the impact beat, which
  //     every dive carries, authored or not. This is what the story CHANGES, not what it counts.
  push("What does this change?", clean(dive?.beats.find((b) => b.phase === "impact")?.line), "next");

  // 5. Plain-language jargon notes.
  for (const n of card.notes || []) {
    if (n.term && n.note) push(`What does "${clean(n.term)}" mean?`, n.note, "term");
  }

  return out.slice(0, Math.max(0, max));
}

/** Whether a card has any grounded follow-ups (drives the chip row). */
export function hasFollowUps(
  card: Parameters<typeof deriveFollowUps>[0],
): boolean {
  return deriveFollowUps(card, 1).length > 0;
}
